import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { AutoAwesome as AutoAwesomeIcon } from '../icons';
import { ChevronRight as ChevronRightIcon } from '../icons';
import { Email as EmailIcon } from '../icons';
import { ExpandMore as ExpandMoreIcon } from '../icons';
import { Send as SendIcon } from '../icons';
import { SupportAgent as SupportAgentIcon } from '../icons';
import { auth, db } from '../firebase';
import { brandColors } from '../theme';
import { useSupportAssistant } from './SupportAssistantContext';
import { onAccent } from './supportChatStyles';
import { T } from './Translate';
import PageHeader from './PageHeader';
import supportFaqData from './supportFaqData';

const cardSx = {
  borderRadius: '16px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const SectionTitle = ({ children }) => (
  <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'text.primary', mb: 1.25 }}>
    {children}
  </Typography>
);

const IconTile = ({ icon: Icon }) => (
  <Box
    aria-hidden="true"
    sx={{
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: '12px',
      display: 'grid',
      placeItems: 'center',
      bgcolor: alpha(brandColors.accent, 0.14),
      color: brandColors.accent,
    }}
  >
    <Icon sx={{ fontSize: 22 }} />
  </Box>
);

const ActionCard = ({ icon, title, description, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      ...cardSx,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      width: '100%',
      textAlign: 'left',
      color: 'text.primary',
      transition: 'border-color 0.15s',
      '&:hover': { borderColor: brandColors.accent },
    }}
  >
    <IconTile icon={icon} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'inherit', lineHeight: 1.3 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>{description}</Typography>
    </Box>
    <ChevronRightIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
  </ButtonBase>
);

const Support = () => {
  const { openAssistant } = useSupportAssistant();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'sent' | 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResult(null);
    try {
      await db.collection('supportMessages').add({
        ...formData,
        userId: auth.currentUser ? auth.currentUser.uid : null,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        handled: false,
      });
      setResult('sent');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting support message:', error);
      setResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PageHeader title={<T>Centro de Soporte</T>} />
      <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', px: 2, pt: 3, pb: 12 }}>
        <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'text.primary', lineHeight: 1.25, mb: 0.5 }}>
          <T>¿En qué podemos ayudarte?</T>
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
          <T>Preguntale al asistente, hablá con una persona del equipo o buscá tu respuesta en las preguntas frecuentes.</T>
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 4 }}>
          <ActionCard
            icon={AutoAwesomeIcon}
            title={<T>Asistente IA de Soporte</T>}
            description={<T>Recibí ayuda inmediata con nuestro asistente.</T>}
            onClick={openAssistant}
          />
          <ActionCard
            icon={SupportAgentIcon}
            title={<T>Hablar con una persona</T>}
            description={<T>Te atiende una persona del equipo.</T>}
            onClick={() => navigate('/support/chat', { state: { requestHuman: true } })}
          />
        </Box>

        <Box component="section" sx={{ mb: 4 }}>
          <SectionTitle><T>Preguntas Frecuentes</T></SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {supportFaqData.map((category) => (
              <Accordion
                key={category.id}
                disableGutters
                elevation={0}
                TransitionProps={{ unmountOnExit: true }}
                sx={{
                  ...cardSx,
                  backgroundImage: 'none',
                  overflow: 'hidden',
                  // El Accordion de MUI recorta las esquinas del primero y el último.
                  '&&&': { borderRadius: '16px' },
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { borderColor: alpha(brandColors.accent, 0.5) },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                  sx={{ px: 1.25, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.25, my: 1 } }}
                >
                  <IconTile icon={category.icon} />
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {category.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.75, pt: 0, pb: 1.75 }}>
                  {category.questions.map((item, index) => (
                    <Box key={index} sx={{ pt: index === 0 ? 0.5 : 1.5, mt: index === 0 ? 0 : 1.5, borderTop: index === 0 ? 0 : 1, borderColor: 'divider' }}>
                      <Typography component="h3" sx={{ fontSize: '0.88rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.35, mb: 0.5 }}>
                        {item.q}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.55 }}>{item.a}</Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        <Box component="section">
          <SectionTitle><T>Escribinos</T></SectionTitle>
          <Box component="form" onSubmit={handleSubmit} sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
            <TextField size="small" name="name" label={<T>Tu nombre</T>} value={formData.name} onChange={handleChange} required fullWidth />
            <TextField size="small" type="email" name="email" label={<T>Tu email</T>} value={formData.email} onChange={handleChange} required fullWidth />
            <TextField name="message" label={<T>Tu mensaje</T>} value={formData.message} onChange={handleChange} required fullWidth multiline minRows={4} maxRows={10} />
            {result === 'sent' && (
              <Alert severity="success"><T>¡Gracias por tu mensaje! Te vamos a responder pronto.</T></Alert>
            )}
            {result === 'error' && (
              <Alert severity="error"><T>Hubo un error al enviar tu mensaje. Intentá de nuevo.</T></Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={isSubmitting}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-end' },
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                boxShadow: 'none',
                bgcolor: brandColors.accent,
                color: onAccent,
                '&:hover': { bgcolor: brandColors.accent, opacity: 0.88, boxShadow: 'none' },
              }}
            >
              {isSubmitting ? <T>Enviando...</T> : <T>Enviar Mensaje</T>}
            </Button>
          </Box>
          <Typography sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 2.5, fontSize: '0.85rem', color: 'text.secondary' }}>
            <EmailIcon sx={{ fontSize: 16 }} />
            <T>También podés escribirnos a</T>
            <Link href="mailto:support@notiline.com" underline="hover" sx={{ color: brandColors.accent, fontWeight: 600 }}>
              support@notiline.com
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Support;
